// A bride/groom family's primary and secondary contacts. Every communication — emails,
// notifications, messages — goes to both; the secondary contact receives it by email.
import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Paper, Stack, Typography, Button, Grid, TextField, Alert, Chip, Box, MenuItem,
} from '@mui/material';
import ContactsIcon from '@mui/icons-material/Contacts';
import { MY_FAMILY_CONTACTS_QUERY, SET_FAMILY_CONTACTS_MUTATION } from '../graphql/queries';

const RELATIONS = ['Bride', 'Groom', 'Mother', 'Father', 'Sister', 'Brother', 'Uncle', 'Aunt', 'Grandparent', 'Cousin', 'Friend', 'Guardian', 'Other'];

const toForm = (c) => ({
  primaryName: c?.primary?.name || '', primaryRelation: c?.primary?.relation || '', primaryPhone: c?.primary?.phone || '',
  secondaryName: c?.secondary?.name || '', secondaryRelation: c?.secondary?.relation || '',
  secondaryEmail: c?.secondary?.email || '', secondaryPhone: c?.secondary?.phone || '',
});

function ContactView({ label, c }) {
  return (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary">{label}</Typography>
      {c ? (
        <>
          <Typography fontWeight={600}>{c.name}{c.relation ? ` (${c.relation})` : ''}</Typography>
          <Typography variant="body2" color="text.secondary">{[c.email, c.phone].filter(Boolean).join(' · ')}</Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">Not added yet</Typography>
      )}
    </Box>
  );
}

// compact: just a prompt to add the secondary contact when missing (dashboard)
export default function FamilyContactsCard({ compact = false, onOpen, sx }) {
  const { data, refetch } = useQuery(MY_FAMILY_CONTACTS_QUERY, { fetchPolicy: 'cache-and-network' });
  const contacts = data?.myFamilyContacts;
  const [form, setForm] = useState(null); // null: not editing
  const [error, setError] = useState('');
  const [save, { loading }] = useMutation(SET_FAMILY_CONTACTS_MUTATION, {
    onCompleted: () => { setForm(null); setError(''); refetch(); },
    onError: (e) => setError(e.message),
  });
  if (!contacts) return null;

  if (compact) {
    if (contacts.secondary) return null;
    return (
      <Alert severity="info" icon={<ContactsIcon />} sx={sx}
        action={onOpen && <Button color="inherit" size="small" onClick={onOpen}>Add contact</Button>}>
        Add a secondary contact (a parent or sibling) — every update, booking and message will be sent to them too.
      </Alert>
    );
  }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, ...sx }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ContactsIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>Contacts</Typography>
        </Stack>
        {!form && <Button size="small" variant="outlined" onClick={() => setForm(toForm(contacts))}>Edit</Button>}
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Every communication — emails, notifications, booking updates and messages — goes to both contacts.
        The secondary contact receives everything by email.
      </Typography>

      {!form ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><ContactView label="PRIMARY (ACCOUNT HOLDER)" c={contacts.primary} /></Grid>
          <Grid item xs={12} sm={6}><ContactView label="SECONDARY" c={contacts.secondary} /></Grid>
        </Grid>
      ) : (
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="subtitle2" fontWeight={700}>Primary contact <Chip size="small" label={contacts.primary.email} sx={{ ml: 1 }} /></Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}><TextField size="small" fullWidth label="Name" value={form.primaryName} onChange={set('primaryName')} /></Grid>
            <Grid item xs={12} sm={3}>
              <TextField size="small" fullWidth select label="Relation" value={form.primaryRelation} onChange={set('primaryRelation')}>
                {RELATIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Phone" value={form.primaryPhone} onChange={set('primaryPhone')} /></Grid>
          </Grid>
          <Typography variant="subtitle2" fontWeight={700}>Secondary contact</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}><TextField size="small" fullWidth label="Name" value={form.secondaryName} onChange={set('secondaryName')} /></Grid>
            <Grid item xs={12} sm={3}>
              <TextField size="small" fullWidth select label="Relation" value={form.secondaryRelation} onChange={set('secondaryRelation')}>
                {RELATIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Phone" value={form.secondaryPhone} onChange={set('secondaryPhone')} /></Grid>
            <Grid item xs={12}>
              <TextField size="small" fullWidth type="email" label="Email" value={form.secondaryEmail} onChange={set('secondaryEmail')}
                helperText="Every communication is sent here too. Leave all secondary fields empty to remove the contact." />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" disabled={loading} onClick={() => save({ variables: { input: form } })}>Save</Button>
            <Button onClick={() => { setForm(null); setError(''); }}>Cancel</Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
}
